import { type AnimationClip } from 'three'

/**
 * Lightweight clip validator: scans an `AnimationClip` for non-finite
 * keyframe values (NaN/Infinity) and other obvious degeneracies that would
 * crash playback or produce garbage poses.
 *
 * Used by the animations list to surface a small warning badge per clip so
 * the user notices broken imports / mocap glitches before exporting.
 */

export interface ClipValidationIssue {
  /** Per-track issue counts; absent if no issues found in that track. */
  track_issues: Array<{ track_name: string, nan_values: number, nan_times: number }>
  /** Total non-finite scalars across all tracks. */
  total_nan: number
  /** True if at least one track is empty (no keyframes). */
  has_empty_track: boolean
  /** True if the clip itself has zero or negative duration. */
  has_invalid_duration: boolean
}

export class ClipValidator {
  static validate (clip: AnimationClip): ClipValidationIssue {
    const issue: ClipValidationIssue = {
      track_issues: [],
      total_nan: 0,
      has_empty_track: false,
      has_invalid_duration: false
    }
    if (!Number.isFinite(clip.duration) || clip.duration < 0) {
      issue.has_invalid_duration = true
    }
    for (const track of clip.tracks) {
      let nan_values = 0
      for (let i = 0; i < track.values.length; i++) {
        if (!Number.isFinite(track.values[i])) nan_values++
      }
      let nan_times = 0
      for (let i = 0; i < track.times.length; i++) {
        if (!Number.isFinite(track.times[i])) nan_times++
      }
      if (track.times.length === 0) issue.has_empty_track = true
      if (nan_values > 0 || nan_times > 0) {
        issue.track_issues.push({ track_name: track.name, nan_values, nan_times })
        issue.total_nan += nan_values + nan_times
      }
    }
    return issue
  }

  /** True if the clip has any issues worth surfacing in the UI. */
  static has_issues (issue: ClipValidationIssue): boolean {
    return issue.total_nan > 0 || issue.has_empty_track || issue.has_invalid_duration
  }

  /** Short user-facing summary, suitable for a tooltip on a warning badge. */
  static summarize (issue: ClipValidationIssue): string {
    const parts: string[] = []
    if (issue.has_invalid_duration) parts.push('invalid clip duration')
    if (issue.has_empty_track) parts.push('one or more tracks have no keyframes')
    if (issue.total_nan > 0) parts.push(`${issue.total_nan} NaN/Infinity value(s) across ${issue.track_issues.length} track(s)`)
    if (parts.length === 0) return 'No issues detected.'
    return `Issues: ${parts.join('; ')}.`
  }
}
